import React, { createContext, useContext, useMemo, useState } from "react";

type UISettingsContextType = {
  autoHideNavigation: boolean;
  setAutoHideNavigation: (enabled: boolean) => void;
};

const STORAGE_KEY = "translatorx_ui_settings";

const UISettingsContext = createContext<UISettingsContextType>({
  autoHideNavigation: false,
  setAutoHideNavigation: () => {},
});

function readInitialSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        autoHideNavigation: false,
      };
    }

    const parsed = JSON.parse(raw) as { autoHideNavigation?: unknown };
    return {
      autoHideNavigation: parsed.autoHideNavigation === true,
    };
  } catch (error) {
    return {
      autoHideNavigation: false,
    };
  }
}

export const UISettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [autoHideNavigation, setAutoHideNavigationState] = useState<boolean>(
    () => readInitialSettings().autoHideNavigation,
  );

  const setAutoHideNavigation = (enabled: boolean) => {
    setAutoHideNavigationState(enabled);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ autoHideNavigation: enabled }),
      );
    } catch (error) {
      // Ignore storage failures and keep the setting in memory.
    }
  };

  const value = useMemo(
    () => ({
      autoHideNavigation,
      setAutoHideNavigation,
    }),
    [autoHideNavigation],
  );

  return (
    <UISettingsContext.Provider value={value}>
      {children}
    </UISettingsContext.Provider>
  );
};

export const useUISettingsContext = () => {
  const context = useContext(UISettingsContext);
  if (!context) {
    throw new Error("useUISettingsContext must be used within a UISettingsProvider");
  }
  return context;
};
